import React, { useEffect, useRef } from 'react';
import styles from './edit-cell.module.scss';
import numberStyles from './numberedit-cell.module.scss';
import { SVGS } from '../../../../../assets/images';

type Props = {
    value?: number | null;
    onChange: (value: number, blur: boolean) => void;
    active: boolean;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: number;
};

const DEFAULT_STEP = 0.5;

const NumberEditCell = ({ value, onChange, active, placeholder, min, max, step }: Props) => {
    const ref = useRef<HTMLInputElement>(null);
    const resolvedStep = step ?? DEFAULT_STEP;
    const valueAsString = String(value ?? '');
    const [draft, setDraft] = React.useState(valueAsString);

    const resetDraft = () => {
        setDraft(valueAsString);
    };

    useEffect(() => {
        resetDraft();
    }, [valueAsString]);

    const normalize = (raw: string): number | null => {
        if (raw.trim() === '') {
            return null;
        }

        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) {
            return null;
        }

        if (min !== undefined && parsed < min) {
            return min;
        }
        if (max !== undefined && parsed > max) {
            return max;
        }

        return parsed;
    };

    const commit = () => {
        const normalized = normalize(draft);

        if (normalized === null) {
            resetDraft();
            return;
        }

        if (normalized !== value) {
            onChange(normalized, true);
        }
    };

    const applyStep = (direction: 1 | -1) => {
        const baseValue = normalize(draft) ?? value ?? min ?? 0;
        const nextValue = normalize(String(baseValue + resolvedStep * direction));

        if (nextValue === null) {
            return;
        }

        setDraft(String(nextValue));
        ref.current?.focus();
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commit();
            ref.current?.blur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            resetDraft();
            ref.current?.blur();
        }
    };

    useEffect(() => {
        if (!active) {
            ref.current?.blur();
        } else {
            ref.current?.focus();
        }
    }, [active]);

    return (
        <div className={numberStyles.numberInputWrapper}>
            <input
                autoFocus
                ref={ref}
                type='number'
                placeholder={placeholder}
                className={`${styles.text} ${numberStyles.numberText}`}
                value={active ? draft : valueAsString}
                min={min}
                max={max}
                step={resolvedStep}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={commit}
            />
            <div className={numberStyles.stepperButtons}>
                <button
                    type='button'
                    className={numberStyles.stepperButton}
                    aria-label='Increase value'
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyStep(1)}>
                    <SVGS.KeyboardArrowDownIcon className={numberStyles.stepperIconUp} />
                </button>
                <button
                    type='button'
                    className={numberStyles.stepperButton}
                    aria-label='Decrease value'
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyStep(-1)}>
                    <SVGS.KeyboardArrowDownIcon className={numberStyles.stepperIconDown} />
                </button>
            </div>
        </div>
    );
};

export default NumberEditCell;
